// Generic record-page form controls + layout primitives. The daily-report
// hotfix established this as the reusable site-wide standard: a consistent
// label/value grid, predictable control widths, aligned boolean controls,
// roomy textareas, and a responsive (stacks on mobile) field row.
//
// Consumers should use these instead of local record-form primitives when a
// surface has a standard label/value edit form. Dense scan-first panels can
// stay local when a guard documents the exception.

import React from 'react';

import {buttonDangerText, buttonPrimary, buttonSecondary} from '../lib/buttonStyles.js';

// ── Responsive field-row grid ───────────────────────────────────────────────
// Desktop: a [label | control] grid with a fixed label column so every row's
// control edge lines up. Mobile (<=640px): stacks the label above a full-width
// control so selects/textareas use the whole card width instead of a cramped
// right column. Injected once as a real CSS class so the media query works
// (inline styles can't carry @media). Consumers apply `recordFieldRowClass`.
const FIELD_ROW_CLASS = 'wcf-record-field-row';
if (typeof document !== 'undefined' && !document.getElementById('wcf-record-controls-css')) {
  const el = document.createElement('style');
  el.id = 'wcf-record-controls-css';
  el.textContent =
    '.' +
    FIELD_ROW_CLASS +
    '{display:grid;grid-template-columns:170px minmax(0,1fr);align-items:center;' +
    'column-gap:16px;padding:8px 0;border-bottom:1px solid var(--divider);font-size:14px;}' +
    '@media (max-width:640px){.' +
    FIELD_ROW_CLASS +
    '{grid-template-columns:1fr;align-items:start;row-gap:4px;}}';
  document.head.appendChild(el);
}
export const recordFieldRowClass = FIELD_ROW_CLASS;

// Style-object equivalent of the desktop grid, for the rare caller that must
// spread it inline (e.g. a computed/read-only row that tweaks the background).
export const recordFieldRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '170px minmax(0, 1fr)',
  alignItems: 'center',
  columnGap: 16,
  padding: '8px 0',
  borderBottom: '1px solid var(--divider)',
  fontSize: 14,
};

// White form card wrapping the editable fields.
export const recordFormCard: React.CSSProperties = {
  background: 'white',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '16px 20px',
};

export const recordFieldLabel: React.CSSProperties = {fontWeight: 600, color: 'var(--ink-muted)', fontSize: 13};

// Predictable control width: fills the value column up to a sane cap.
export const recordControl: React.CSSProperties = {
  fontSize: 14,
  padding: '7px 10px',
  border: '1px solid var(--border-strong)',
  borderRadius: 10,
  fontFamily: 'inherit',
  width: '100%',
  maxWidth: 340,
  boxSizing: 'border-box',
};

// Textareas use the full value-column width and a practical min-height.
export const recordTextarea: React.CSSProperties = {
  ...recordControl,
  maxWidth: '100%',
  minHeight: 96,
  resize: 'vertical',
  lineHeight: 1.4,
};

// Checkbox sized + left-aligned in the value column.
export const recordCheckbox: React.CSSProperties = {width: 18, height: 18, cursor: 'pointer'};

// ── Record-page action buttons ──────────────────────────────────────────────
// The record-page form footer (Save / Revert), the load-error Retry and the
// Delete trigger, all from the one button source (src/lib/buttonStyles, §4).
// Call sites keep their own data hooks, disabled logic, labels, and any
// layout-only override (e.g. marginTop) via a spread.

// Primary save (brand green).
export const recordSaveButton: React.CSSProperties = {...buttonPrimary};

// Secondary action: form Revert and the load-error Retry (F04).
export const recordSecondaryButton: React.CSSProperties = {...buttonSecondary};

// Delete trigger: secondary shape, danger text (O07). The confirm step owns the fill.
export const recordDeleteButton: React.CSSProperties = {...buttonDangerText};

// RecordPageBody maxWidth convention: form-centric record pages (mostly
// label/value fields, like the daily pages) use RECORD_FORM_MAXWIDTH for a
// consistent comfortable column. Dense stats / grid pages (e.g. layer batch
// lifecycle, broiler batch) may stay wider. A page whose RecordPageBody also
// wraps heavy stats/weight grids tuned to a narrower width should keep that
// width until those panels are migrated too — don't widen the whole page just
// to migrate a contained form card.
export const RECORD_FORM_MAXWIDTH = 960;

const lockedTeamMemberBox: React.CSSProperties = {
  ...recordControl,
  background: 'var(--surface-2)',
  borderColor: 'var(--border)',
  color: 'var(--ink)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

// `caption` is explicitly passed as null by every record page (they want the box
// without the "signed in" suffix), which is why it is a node rather than a
// string and why the default only applies when the prop is omitted entirely.
type LockedTeamMemberFieldProps = {
  value?: string | null;
  label?: React.ReactNode;
  labelStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  caption?: React.ReactNode;
};

export function LockedTeamMemberField({
  value,
  label = null,
  labelStyle,
  style,
  caption = 'signed in',
}: LockedTeamMemberFieldProps) {
  const display = value || 'Signed-in user';
  return (
    <div>
      {label != null && <label style={labelStyle}>{label}</label>}
      <div
        data-team-member-select="1"
        data-team-member-select-locked="1"
        style={style ? {...lockedTeamMemberBox, ...style} : lockedTeamMemberBox}
      >
        <span style={{fontWeight: 600}}>{display}</span>
        {caption ? <span style={{fontSize: 11, color: 'var(--ink-faint)'}}>{caption}</span> : null}
      </div>
    </div>
  );
}
